import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import momentTz from 'moment-timezone';
import React, { useState } from "react";
import { Button, Row, Col, Form, Modal, Spinner, Alert } from "react-bootstrap";
import CardInput from "../../../Components/CardInput";
import { STRIPR_WARNING_MSG } from "../../../utils";
import { diplayCreditCard } from "../Cards/Utils";
import { required, requiredIf, paymentFieldsReqCallback, EDIT_SUBSCRIPTION_TYPES } from "./AddSubscription";

const addPaymentValidators = [
    {
        key: "addSubscriptionType",
        validators: [required],
    },
    {
        key: "cardId",
        validators: [requiredIf(paymentFieldsReqCallback("cardId"))]
    }
]

const getInitialPaymentDate = (subscription)=>{
    const subsTimeZone = subscription?.subscriptionType?.timezone;
    const paymentDate = momentTz.tz(subscription?.validityEndDate,subsTimeZone).add(1,'day');
    const year = paymentDate.year();
    const month = paymentDate.month();
    const date = paymentDate.date();
    return new Date(year, month, date);
}

const EditPayment = ({ show, handleClose, subscription, subscriptionTypes,  handleSuccess, cards, subsCard, stripeKey }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [formData, setFormData] = useState({ values: { paymentDate: getInitialPaymentDate(subscription) }, errors: {} });
    const [submitting, setSubmitting] = useState(false);
    const [stripeError, setStripeError] = useState(undefined);
    const [apiError, setApiError] = useState(undefined);    
    
    const validateForm = (data)=>{
        const errors = {};
        addPaymentValidators.forEach(({ key, validators }) => {
            const value = data[key];
            let error;
            validators.forEach(validator => {
                if (!error) {
                    error = validator(value, data);
                }
            });
            if (error) {
                errors[key] = error;
            }
        });
        return errors;
    }

    const handleChange = (field, value) => {
        setFormData((prevFormData) => {
            const { values: prevValues, errors: prevErrors } = prevFormData;
            const validators = addPaymentValidators.find(({key})=>(key === field))?.validators || [];
            let error;
            validators.forEach(validator => {
                if (!error) {
                    error = validator(value);
                }
            });
            return {
                ...prevFormData,
                values: { ...prevValues, [field]: value },
                errors: { ...prevErrors, [field]: error}
            }
        });
    }

    const onSubmit = async () => {
        const { values: data } = formData;

        const errors = validateForm(data);

        if (Object.keys(errors).length > 0) {
            setFormData((prevFormData) => {
                const { errors: prevErrors } = prevFormData;
                return {
                    ...prevFormData,
                    errors: { ...prevErrors, ...errors }
                }
            })
            return;
        }

        const {addSubscriptionType} = data;
        let newCardToken;
        setSubmitting(true);
        setApiError(undefined);
        setStripeError(undefined);
        if(addSubscriptionType === "NEW_CARD"){
            if (!stripe || !elements) {
                // Stripe.js has not yet loaded.
                // Make sure to disable form submission until Stripe.js has loaded.
                setStripeError("Stripe hasn't loaded yet.");
                setSubmitting(false);
                return;
            }
            const { token:data, error }  = await stripe.createToken(elements.getElement(CardElement));
            if (error) {
                setStripeError(error.message);
                setSubmitting(false);
                return;
            }
            newCardToken = data.id;
        }

        const sanitizedData = {
            userSubscriptionId: subscription?.id,
            paymentType: data?.addSubscriptionType,
            cardId: data?.cardId,
            newCardToken, 
            saveCard: true,            
        };

        try {
            await handleSuccess(sanitizedData);
            setSubmitting(false);
        } catch (e) {
            setApiError(e?.message || "System error");
            setSubmitting(false);
            console.log(e)
        }

    }
    const addSubscriptionType = formData?.values?.addSubscriptionType;
    const noExistingCards = !(cards?.length !== 0);
    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                {subsCard && (<Modal.Title> Edit Card for {subscription?.subscriptionType?.name}</Modal.Title>)}
                {!subsCard && (<Modal.Title> Add a Card for {subscription?.subscriptionType?.name}</Modal.Title>)}
            </Modal.Header>
            <Modal.Body>
                <Form >
                    <Form.Group as={Row} className="mb-3" controlId="frmAddSubscriptionType">
                        <Form.Label column sm={3}>Payment Method*</Form.Label>
                        <Col sm={9}>
                            <Form.Select
                                value={addSubscriptionType}
                                onChange={(e) => handleChange('addSubscriptionType', e.target.value)}
                                isInvalid={formData?.errors?.addSubscriptionType}
                            >
                                <option value="">--</option>
                                {EDIT_SUBSCRIPTION_TYPES.map(st => {
                                    const isExistingOpt = st.value === "EXISTING_CARD";
                                    const isDisabled = isExistingOpt && noExistingCards;
                                    return (<option
                                        key={st.value}
                                        value={st.value}
                                        disabled={isDisabled}
                                    >
                                        {`${st.label}${isDisabled ? ' (No cards found)': ''}`}
                                    </option>)
                                })}
                            </Form.Select>
                            {formData?.errors?.addSubscriptionType && (<span className="error-text">
                                {formData?.errors?.addSubscriptionType}
                            </span>)}
                        </Col>
                    </Form.Group>
                    {(addSubscriptionType === "EXISTING_CARD")&& (
                         <Form.Group as={Row} className="mb-3" controlId="frmCardId">
                         <Form.Label column sm={3}>Credit Card*</Form.Label>
                         <Col sm={9}>
                             <Form.Select
                                 value={formData?.values?.cardId}
                                 onChange={(e) => handleChange('cardId', e.target.value)}
                                 isInvalid={formData?.errors?.cardId}
                             >
                                 <option value="">--</option>
                                 {cards.map(card => (<option key={card.id} value={card.id}>{diplayCreditCard(card)}</option>))}
                             </Form.Select>
                             {formData?.errors?.cardId && (<span className="error-text">
                                 {formData?.errors?.cardId}
                             </span>)}
                         </Col>
                     </Form.Group>
                    )}
                    {(addSubscriptionType === "NEW_CARD")&& (
                        <>
                            <Form.Group as={Row} className="mb-3" controlId="frmNewCardToken">
                                {(!stripeKey) && (
                                    <Alert variant="warning">{STRIPR_WARNING_MSG}</Alert>
                                )}
                                <Form.Label column sm={3}>New Credit Card*</Form.Label>
                                <Col sm={9}>
                                    <div className="border-box">
                                        <CardInput/>
                                    </div>
                                    {stripeError && (<span className="error-text">
                                        {stripeError}
                                    </span>)}
                                </Col>
                            </Form.Group>
                        </>
                    )}
                </Form>
                {apiError && (
                    <Alert variant="danger">{apiError}</Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                {subsCard && (<Button variant="primary" onClick={onSubmit} disabled={submitting}>
                    {submitting && <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />}
                    Edit payment method
                </Button>)}
                {!subsCard && (<Button variant="primary" onClick={onSubmit} disabled={submitting}>
                    {submitting && <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />}
                    Add payment method
                </Button>)}                
            </Modal.Footer>
        </Modal>
    )
}

export default EditPayment;